/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import React from "react";
import BASEScrollbars from "../BASEScrollbars";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import * as PropTypes from "prop-types";
import { forwardRef, memo, useImperativeHandle, useRef, useEffect } from "react";
import GlobalStyles from "@mui/material/GlobalStyles";
import BASEPageSimpleHeader from "./BASEPageSimpleHeader";
import BASEPageSimpleSidebar from "./BASEPageSimpleSidebar";

const Root = styled("div")(({ theme, ...props }) => ({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  minHeight: "100%",
  position: "relative",
  flex: "1 1 auto",
  width: "100%",
  height: "auto",
  backgroundColor: theme.palette.background.default,

  "&.BASEPageSimple-scroll-content": {
    height: "100%",
  },

  "& .BASEPageSimple-wrapper": {
    display: "flex",
    flexDirection: "row",
    flex: "1 1 auto",
    zIndex: 2,
    minWidth: 0,
    height: "100%",
    backgroundColor: theme.palette.background.default,

    ...(scroll === "content" && {
      position: "absolute",
      top: 0,
      bottom: 0,
      right: 0,
      left: 0,
      overflow: "hidden",
    }),
  },

  "& .BASEPageSimple-header": {
    display: "flex",
    flex: "0 0 auto",
    backgroundSize: "cover",
  },

  "& .BASEPageSimple-topBg": {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    height: headerHeight,
    pointerEvents: "none",
  },

  "& .BASEPageSimple-contentWrapper": {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    flex: "1 1 auto",
    overflow: "hidden",
    //    WebkitOverflowScrolling: 'touch',
    zIndex: 9999,
  },

  "& .BASEPageSimple-toolbar": {
    height: toolbarHeight,
    minHeight: toolbarHeight,
    display: "flex",
    alignItems: "center",
  },

  "& .BASEPageSimple-content": {
    display: "flex",
    flex: "1 1 auto",
    alignItems: "start",
    minHeight: 0,
    overflowY: "auto",
  },

  "& .BASEPageSimple-sidebarWrapper": {
    overflow: "hidden",
    backgroundColor: "transparent",
    position: "absolute",
    "&.permanent": {
      [theme.breakpoints.up("lg")]: {
        position: "relative",
        marginLeft: 0,
        marginRight: 0,
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        "&.closed": {
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),

          "&.BASEPageSimple-leftSidebar": {
            marginLeft: -props.leftsidebarwidth,
          },
          "&.BASEPageSimple-rightSidebar": {
            marginRight: -props.rightsidebarwidth,
          },
        },
      },
    },
  },

  "& .BASEPageSimple-sidebar": {
    position: "absolute",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,

    "&.permanent": {
      [theme.breakpoints.up("lg")]: {
        position: "relative",
      },
    },
    maxWidth: "100%",
    height: "100%",
  },

  "& .BASEPageSimple-leftSidebar": {
    width: props.leftsidebarwidth,

    [theme.breakpoints.up("lg")]: {
      borderRight: `1px solid ${theme.palette.divider}`,
      borderLeft: 0,
    },
  },

  "& .BASEPageSimple-rightSidebar": {
    width: props.rightsidebarwidth,

    [theme.breakpoints.up("lg")]: {
      borderLeft: `1px solid ${theme.palette.divider}`,
      borderRight: 0,
    },
  },

  "& .BASEPageSimple-sidebarHeader": {
    height: headerHeight,
    minHeight: headerHeight,
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
  },

  "& .BASEPageSimple-sidebarHeaderInnerSidebar": {
    backgroundColor: "transparent",
    color: "inherit",
    height: "auto",
    minHeight: "auto",
  },

  "& .BASEPageSimple-sidebarContent": {
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
  },

  "& .BASEPageSimple-backdrop": {
    position: "absolute",
  },
}));

const headerHeight = 120;
const toolbarHeight = 64;

const BASEPageSimple = forwardRef((props, ref) => {
  const { 
    scroll = "page",
    className,
    header,
    content,
    leftSidebarContent,
    rightSidebarContent,
    leftSidebarOpen = false,
    rightSidebarOpen = false,
    rightSidebarWidth = 240,
    leftSidebarWidth = 240,
    leftSidebarVariant = "permanent",
    rightSidebarVariant = "permanent",
    handleRightSidebarOnClose,
    handleLeftSidebarOnClose,
  } = props;
  // console.info("render::BASEPageSimple");
  const leftSidebarRef = useRef(null);
  const rightSidebarRef = useRef(null);
  const rootRef = useRef(null);

  useImperativeHandle(ref, () => ({
    rootRef,
    toggleLeftSidebar: (val) => {
      leftSidebarRef.current.toggleSidebar(val);
    },
    toggleRightSidebar: (val) => {
      rightSidebarRef.current.toggleSidebar(val);
    },
  }));

  return (
    <>
      <GlobalStyles
        styles={(theme) => ({
          ...(scroll !== "page" && {
            "#base-toolbar": {
              position: "static",
            },
            "#base-footer": {
              position: "static",
            },
          }),
          ...(scroll === "page" && {
            "#base-toolbar": {
              position: "sticky",
              top: 0,
            },
            "#base-footer": {
              position: "sticky",
              bottom: 0,
            },
          }),
        })}
      />
      <Root
        className={clsx(
          "BASEPageSimple-root",
          `BASEPageSimple-scroll-${scroll}`,
          className
        )}
        ref={rootRef}
        scroll={scroll}
        leftsidebarwidth={leftSidebarWidth}
        rightsidebarwidth={rightSidebarWidth}
      >
        <div className="flex flex-auto flex-col z-10 h-full">
          <div className="BASEPageSimple-wrapper">
            {leftSidebarContent && (
              <BASEPageSimpleSidebar
                position="left"
                content={leftSidebarContent}
                variant={leftSidebarVariant || "permanent"}
                ref={leftSidebarRef}
                rootRef={rootRef}
                open={leftSidebarOpen}
                onClose={handleLeftSidebarOnClose}
              />
            )}

            <div
              className="BASEPageSimple-contentWrapper"
              // enable={scroll === 'page'}
            >
              {header && <BASEPageSimpleHeader header={header} />}
              {content && (
                <BASEScrollbars
                  enable={scroll === "content"}
                  className={clsx("BASEPageSimple-content")}
                >
                  {content}
                </BASEScrollbars>
              )}
            </div>

            {rightSidebarContent && (
              <BASEPageSimpleSidebar
                position="right"
                content={rightSidebarContent}
                variant={rightSidebarVariant || "permanent"}
                ref={rightSidebarRef}
                rootRef={rootRef}
                open={rightSidebarOpen}
                onClose={handleRightSidebarOnClose}
              />
            )}
          </div>
        </div>
      </Root>
    </>
  );
});

BASEPageSimple.propTypes = {
  leftSidebarContent: PropTypes.node,
  leftSidebarVariant: PropTypes.node,
  rightSidebarContent: PropTypes.node,
  rightSidebarVariant: PropTypes.node,
  header: PropTypes.node,
  content: PropTypes.node,
  scroll: PropTypes.oneOf(["normal", "page", "content"]),
  leftSidebarOpen: PropTypes.bool,
  rightSidebarOpen: PropTypes.bool,
  leftSidebarWidth: PropTypes.number,
  rightSidebarWidth: PropTypes.number,
  handleRightSidebarOnClose: PropTypes.func,
  handleLeftSidebarOnClose: PropTypes.func,
};

// BASEPageSimple.defaultProps = {
//   classes: {},
//   scroll: "page",
//   leftSidebarOpen: false,
//   rightSidebarOpen: false,
//   rightSidebarWidth: 240,
//   leftSidebarWidth: 240,
// };

export default memo(styled(BASEPageSimple)``);
